import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/Toast';
import { formatDate } from '@/shared/utils/format';
import { agencyApi } from '../../api/agencyApi';
import { communityAdminApi, type CommunitySettingsPatch, type EventSourceRow, type FeedPreview } from '../../api/communityAdminApi';
import { agencyKeys, communityAdminKeys } from '../../queryKeys';
import adminStyles from '../admin/AdminCards.module.css';
import styles from './CommunityModulePanel.module.css';

const RUN_PILL: Record<string, string> = {
  done: styles.pillSuccess,
  awaiting_joe: styles.pillAccent,
  running: styles.pillAccent,
  skipped: styles.pillMuted,
  failed: styles.pillError,
};

function apiErrorMessage(err: unknown, fallback: string): string {
  const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
  return message ?? (err instanceof Error ? err.message : fallback);
}

/**
 * Modules › Community for one location: the feature switch (the same audited flag endpoint every
 * other flag uses), Joe's status (read-only, links to the Agents tab), the per-location settings,
 * event feeds with Test feed, Run scout now, and recent runs.
 */
export function CommunityModulePanel({
  practiceId,
  locationId,
  flags,
}: {
  practiceId: string;
  locationId: string;
  flags: Record<string, boolean>;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const featureOn = flags.community === true;
  const { data, isLoading } = useQuery({
    queryKey: communityAdminKeys.settings(practiceId, locationId),
    queryFn: () => communityAdminApi.getSettings(practiceId, locationId),
  });
  const { data: runs } = useQuery({
    queryKey: communityAdminKeys.runs(practiceId, locationId),
    queryFn: () => communityAdminApi.listRuns(practiceId, locationId),
    refetchInterval: 30_000,
  });
  const { data: eventSources } = useQuery({
    queryKey: communityAdminKeys.eventSources(practiceId, locationId),
    queryFn: () => communityAdminApi.listEventSources(practiceId, locationId),
    refetchInterval: 30_000,
  });
  const refreshSources = () => queryClient.invalidateQueries({ queryKey: communityAdminKeys.eventSources(practiceId, locationId) });
  const discover = useMutation({
    mutationFn: () => communityAdminApi.discoverCalendars(practiceId, locationId),
    onSuccess: () => toast.success('Joe is looking for public calendars near this practice. Proposals appear here within a few minutes.'),
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not start looking for calendars')),
  });
  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'approve' | 'decline' }) => communityAdminApi.decideEventSource(practiceId, locationId, id, decision),
    onSuccess: (row) => {
      refreshSources();
      toast.success(row.status === 'approved' ? 'Calendar approved. The next scan reads it.' : 'Calendar declined. It will not be proposed again.');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not update the calendar')),
  });
  const [sourceUrl, setSourceUrl] = useState('');
  const addSource = useMutation({
    mutationFn: (url: string) => communityAdminApi.addEventSource(practiceId, locationId, url),
    onSuccess: (row) => {
      refreshSources();
      setSourceUrl('');
      toast.success(`Calendar added: ${row.eventsFound} upcoming event${row.eventsFound === 1 ? '' : 's'} found.`);
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not add the calendar')),
  });

  const flagMutation = useMutation({
    mutationFn: (next: boolean) => agencyApi.updatePracticeFlags(practiceId, { community: next }),
    onSuccess: (_r, next) => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.practice(practiceId) });
      queryClient.invalidateQueries({ queryKey: communityAdminKeys.settings(practiceId, locationId) });
      queryClient.invalidateQueries({ queryKey: ['agency', 'command-center'] });
      toast.success(next ? 'Community switched on for this practice. Joe stays off until you switch him on under Agents.' : 'Community switched off. Joe is off too; nothing was deleted.');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not update the flag')),
  });
  const save = useMutation({
    mutationFn: (patch: CommunitySettingsPatch) => communityAdminApi.updateSettings(practiceId, locationId, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityAdminKeys.settings(practiceId, locationId) });
      toast.success('Community settings saved.');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not save the settings')),
  });
  const runNow = useMutation({
    mutationFn: () => communityAdminApi.runScoutNow(practiceId, locationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityAdminKeys.runs(practiceId, locationId) });
      toast.success('Scout run queued. Picks appear once Joe writes the why lines.');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not start a scout run')),
  });
  const testFeed = useMutation({
    mutationFn: (url: string) => communityAdminApi.testFeed(url),
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not read the feed')),
  });

  const [feedUrl, setFeedUrl] = useState('');
  const [preview, setPreview] = useState<FeedPreview | null>(null);
  const [picksPerWeek, setPicksPerWeek] = useState(15);
  useEffect(() => {
    if (data) setPicksPerWeek(data.settings.picksPerWeek);
  }, [data]);

  const settings = data?.settings;
  const options = data?.options;
  const joeActive = data?.joeActive ?? false;
  const toggleList = (key: 'enabledCategories' | 'enabledEventTypes', value: string) => {
    if (!settings) return;
    const current = settings[key];
    save.mutate({ [key]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value] });
  };

  return (
    <>
      <Card title="Feature switch">
        <div className={adminStyles.aiActions}>
          <div className={adminStyles.aiActionRow}>
            <div>
              <div className={adminStyles.aiActionLabel}>Community &amp; Referrals</div>
              <div className={adminStyles.aiActionDesc}>
                Adds the Community page (Joe's picks and My Network) to this practice. Off by default. Turning it off hides the page, Joe's tasks and cards, stops every Community job and switches Joe off; nothing is deleted. Turning it back on does not switch Joe back on.
              </div>
            </div>
            <Button variant={featureOn ? 'secondary' : 'primary'} size="sm" loading={flagMutation.isPending} onClick={() => flagMutation.mutate(!featureOn)}>
              {featureOn ? 'Disable' : 'Enable'}
            </Button>
          </div>
          <div className={adminStyles.aiActionRow}>
            <div>
              <div className={adminStyles.aiActionLabel}>Joe, the Community Agent</div>
              <div className={adminStyles.aiActionDesc}>
                {joeActive ? 'On for this practice: monthly picks, weekly reminders of planned touches, reconnect reminders, drafts and the monthly check-in run.' : featureOn ? 'Off. Hire Joe and switch on "Practice can see tasks" under the Agents tab.' : 'Off. Turn on the Community module first; the Agents tab keeps Joe disabled until then.'}
              </div>
            </div>
            <Link to={`/agency/practices/${practiceId}?tab=agents&location=${locationId}`} className={styles.note}>
              Open Agents tab
            </Link>
          </div>
        </div>
      </Card>

      {isLoading || !settings || !options ? (
        <Skeleton height={200} borderRadius="var(--card-radius)" />
      ) : (
        <>
          <Card title="Settings" subtitle="Seeded from the practice type. The owner can change the radius and partner chips from their page; staff set everything here.">
            <div className={styles.rows}>
              <div className={styles.row}>
                <div>
                  <div className={styles.label}>Practice type</div>
                  <div className={styles.desc}>Which partners and events Joe looks for. Prefilled from the practice's vertical and specialty; changing it re-seeds the chips below.</div>
                  <div className={styles.desc}>{settings.rationale}</div>
                </div>
                <div className={styles.control}>
                  <Select
                    aria-label="Practice type"
                    value={settings.practiceType}
                    disabled={save.isPending}
                    options={options.practiceTypes.map((t) => ({ value: t.key, label: `${t.verticalLabel}: ${t.label}${t.recommended ? ' (recommended)' : ''}` }))}
                    onChange={(e) => save.mutate({ practiceType: e.target.value })}
                    fullWidth
                  />
                </div>
              </div>
              <div className={styles.row}>
                <div>
                  <div className={styles.label}>Default radius</div>
                  <div className={styles.desc}>Miles around the location's address. Saving triggers a re-scan when Joe is on.</div>
                </div>
                <div className={styles.chips}>
                  {options.radii.map((r) => (
                    <button key={r} type="button" className={clsx(styles.chip, settings.radiusMi === r && styles.chipActive)} disabled={save.isPending} onClick={() => save.mutate({ radiusMi: r })}>
                      {r} mi
                    </button>
                  ))}
                </div>
              </div>
              <div className={clsx(styles.row, styles.rowStack)}>
                <div>
                  <div className={styles.label}>Partner categories</div>
                  <div className={styles.desc}>Who Joe looks for. Clinical categories come from the NPI registry; the rest from Google Places (skipped when no Places key is set).</div>
                </div>
                <div className={styles.chips}>
                  {options.categories.map((c) => (
                    <button key={c.key} type="button" className={clsx(styles.chip, settings.enabledCategories.includes(c.key) && styles.chipActive)} aria-pressed={settings.enabledCategories.includes(c.key)} disabled={save.isPending} onClick={() => toggleList('enabledCategories', c.key)}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={clsx(styles.row, styles.rowStack)}>
                <div>
                  <div className={styles.label}>Event types</div>
                  <div className={styles.desc}>Which kinds of events from the feeds below make the cut.</div>
                </div>
                <div className={styles.chips}>
                  {options.eventTypes.map((e) => (
                    <button key={e.key} type="button" className={clsx(styles.chip, settings.enabledEventTypes.includes(e.key) && styles.chipActive)} aria-pressed={settings.enabledEventTypes.includes(e.key)} disabled={save.isPending} onClick={() => toggleList('enabledEventTypes', e.key)}>
                      {e.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles.row}>
                <div>
                  <div className={styles.label}>Picks per month</div>
                  <div className={styles.desc}>6 to 30 per monthly scan. Owners look in monthly or quarterly, so each scan brings a fuller list.</div>
                </div>
                <div className={styles.feedAdd}>
                  <Input type="number" min={6} max={30} value={picksPerWeek} onChange={(e) => setPicksPerWeek(Number(e.target.value))} aria-label="Picks per month" />
                  <Button variant="secondary" size="sm" disabled={save.isPending || picksPerWeek === settings.picksPerWeek} onClick={() => save.mutate({ picksPerWeek })}>
                    Save
                  </Button>
                </div>
              </div>
              <div className={styles.row}>
                <div>
                  <div className={styles.label}>Monthly referral check-in</div>
                  <div className={styles.desc}>Joe asks for owner-confirmed counts per partner on the first business day. Counts only, never patient details.</div>
                </div>
                <Button variant={settings.referralCheckinOn ? 'secondary' : 'primary'} size="sm" disabled={save.isPending} onClick={() => save.mutate({ referralCheckinOn: !settings.referralCheckinOn })}>
                  {settings.referralCheckinOn ? 'Turn off' : 'Turn on'}
                </Button>
              </div>
              <div className={styles.row}>
                <div>
                  <div className={styles.label}>Peer networking</div>
                  <div className={styles.desc}>Include same-specialty practices in discovery. Off by default.</div>
                </div>
                <Button variant={settings.peerNetworking ? 'secondary' : 'primary'} size="sm" disabled={save.isPending} onClick={() => save.mutate({ peerNetworking: !settings.peerNetworking })}>
                  {settings.peerNetworking ? 'Turn off' : 'Turn on'}
                </Button>
              </div>
            </div>
          </Card>

          <Card
            title="Calendars Joe found"
            subtitle="Joe looks for the public calendars of the organisations near the practice (downtown association, chamber, city, parks, schools, library, county health, hospital foundation, dental or medical society) when he is switched on and each quarter. Nothing is read by a scan until you approve it here."
            action={
              <Button variant="secondary" size="sm" disabled={!joeActive || discover.isPending} loading={discover.isPending} title={joeActive ? undefined : 'Needs the module on and Joe on'} onClick={() => discover.mutate()}>
                Find calendars now
              </Button>
            }
          >
            <div className={styles.rows}>
              {(!eventSources || eventSources.length === 0) && <div className={styles.note}>Nothing found yet. Joe looks when he is switched on; press Find calendars now to look again.</div>}
              {(['proposed', 'approved', 'declined'] as const).map((status) => {
                const rows = (eventSources ?? []).filter((s) => s.status === status);
                if (rows.length === 0) return null;
                return (
                  <div key={status} className={styles.rowStack}>
                    <div className={styles.label}>{status === 'proposed' ? `Waiting for approval (${rows.length})` : status === 'approved' ? `Approved (${rows.length})` : `Declined (${rows.length})`}</div>
                    {rows.map((row: EventSourceRow) => (
                      <div key={row.id} className={styles.sourceRow}>
                        <div className={styles.sourceMain}>
                          <div>
                            <strong>{row.orgName ?? row.url}</strong>
                            {' · '}
                            <span className={styles.pill}>{row.kind === 'jsonld' ? 'events page' : row.kind === 'gcal' ? 'Google Calendar' : row.kind.toUpperCase()}</span>
                            {' · '}
                            {row.eventsFound} upcoming event{row.eventsFound === 1 ? '' : 's'}
                            {row.discoveredVia === 'owner_event' ? ' · from an event the owner added' : row.discoveredVia === 'staff' ? ' · added by staff' : ''}
                          </div>
                          <div className={styles.feedUrl} title={row.url}>
                            <a href={row.url} target="_blank" rel="noreferrer">
                              {row.url}
                            </a>
                          </div>
                          {row.sampleTitles.length > 0 && <div className={styles.desc}>{row.sampleTitles.join(' · ')}</div>}
                        </div>
                        {status !== 'approved' && (
                          <Button variant="primary" size="sm" disabled={decide.isPending} onClick={() => decide.mutate({ id: row.id, decision: 'approve' })}>
                            Approve
                          </Button>
                        )}
                        {status !== 'declined' && (
                          <Button variant="ghost" size="sm" disabled={decide.isPending} onClick={() => decide.mutate({ id: row.id, decision: 'decline' })}>
                            {status === 'approved' ? 'Remove' : 'Decline'}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
              <div className={styles.feedAdd}>
                <Input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="https://example.org/events/ (an events page, iCal, RSS or a page with a Google Calendar)" aria-label="Calendar or events page URL" fullWidth />
                <Button variant="primary" size="sm" disabled={!sourceUrl.trim() || addSource.isPending} loading={addSource.isPending} onClick={() => addSource.mutate(sourceUrl.trim())}>
                  Check and add
                </Button>
              </div>
            </div>
          </Card>

          <Card title="Event feeds" subtitle="iCal or RSS URLs pasted by hand: chamber, city and parks, school district, dental society calendars. Public calendars and event pages are read and cited; nothing behind a login.">
            <div className={styles.rows}>
              {settings.eventFeedUrls.length === 0 && <div className={styles.note}>No feeds yet. Without feeds Joe still finds partners; events need at least one feed.</div>}
              {settings.eventFeedUrls.map((url) => (
                <div key={url} className={styles.feedRow}>
                  <span className={styles.feedUrl} title={url}>
                    {url}
                  </span>
                  <Button variant="secondary" size="sm" loading={testFeed.isPending && testFeed.variables === url} onClick={() => testFeed.mutate(url, { onSuccess: setPreview })}>
                    Test feed
                  </Button>
                  <Button variant="ghost" size="sm" disabled={save.isPending} onClick={() => save.mutate({ eventFeedUrls: settings.eventFeedUrls.filter((u) => u !== url) })}>
                    Remove
                  </Button>
                </div>
              ))}
              <div className={styles.feedAdd}>
                <Input value={feedUrl} onChange={(e) => setFeedUrl(e.target.value)} placeholder="https://example.org/calendar.ics" aria-label="Feed URL" fullWidth />
                <Button variant="secondary" size="sm" disabled={!feedUrl.trim() || testFeed.isPending} onClick={() => testFeed.mutate(feedUrl.trim(), { onSuccess: setPreview })}>
                  Test feed
                </Button>
                <Button variant="primary" size="sm" disabled={!feedUrl.trim() || save.isPending} onClick={() => save.mutate({ eventFeedUrls: [...settings.eventFeedUrls, feedUrl.trim()] }, { onSuccess: () => setFeedUrl('') })}>
                  Add
                </Button>
              </div>
              {preview && (
                <div className={styles.preview}>
                  <strong>{preview.count} upcoming event{preview.count === 1 ? '' : 's'} in the next 45 days</strong>
                  {preview.events.slice(0, 8).map((e, i) => (
                    <span key={i}>
                      {formatDate(e.start, 'EEE MMM d')} · {e.title}
                      {e.organizer ? ` · ${e.organizer}` : ''} · {e.format.replace(/_/g, ' ')}
                      {e.hasCost ? ' · costs money' : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </Card>

          <Card
            title="Scout runs"
            subtitle="Monthly on the first business day at 06:00 local, on settings changes, and on demand."
            action={
              <Button variant="secondary" size="sm" disabled={!joeActive || runNow.isPending} loading={runNow.isPending} title={joeActive ? undefined : 'Needs the module on and Joe on'} onClick={() => runNow.mutate()}>
                Run scout now
              </Button>
            }
          >
            <div className={styles.runs}>
              {!runs || runs.length === 0 ? (
                <div className={styles.note}>No runs yet.</div>
              ) : (
                runs.map((run) => (
                  <div key={run.id} className={styles.run}>
                    <div>
                      <div>
                        Period {run.weekOf} · {run.trigger.replace(/_/g, ' ')} · {run.candidates} candidate{run.candidates === 1 ? '' : 's'}, {run.picks} pick{run.picks === 1 ? '' : 's'}
                      </div>
                      {run.note && <div className={styles.runNote}>{run.note}</div>}
                      {run.sources && (
                        <div className={styles.runNote}>
                          {Object.entries(run.sources)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(' · ')}
                        </div>
                      )}
                    </div>
                    <span className={styles.runNote}>{formatDate(run.startedAt, 'MMM d, HH:mm')}</span>
                    <span className={clsx(styles.pill, RUN_PILL[run.status] ?? styles.pillMuted)}>{run.status.replace(/_/g, ' ')}</span>
                  </div>
                ))
              )}
            </div>
            <p className={styles.note}>
              Joe's tasks for this practice are listed under{' '}
              <Link to={`/agency/practices/${practiceId}?tab=agents&location=${locationId}`}>Agents › Joe</Link>.
            </p>
          </Card>
        </>
      )}
    </>
  );
}
